/**
 * Profile Store
 * Zustand store for managing user profile data with localStorage persistence
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ProfileData {
  // Personal Info
  fullName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  school: string;
  gradeLevel: string;
  address?: string;
  studentId: string;
  joinDate: string;

  // Avatar
  avatarUrl?: string;

  // Account Security
  twoFactorEnabled: boolean;
  lastPasswordChange?: string;

  // Exam Preferences
  examLanguage: 'en' | 'vi';
  examFontSize: 'small' | 'medium' | 'large';
  autoSaveInterval: number; // seconds
  showTimer: boolean;

  // Notifications
  emailNotifications: boolean;
  examReminders: boolean;
  resultsNotifications: boolean;
  weeklyProgress: boolean;

  // Stats
  totalExams: number;
  averageScore: number;
  studyHours: number;
  achievements: number;
}

export interface LoginHistoryItem {
  id: string;
  device: string;
  location: string;
  date: string;
  ip: string;
}

interface ProfileState {
  profile: ProfileData | null;
  loginHistory: LoginHistoryItem[];
  isLoading: boolean;

  // Actions
  loadProfile: () => Promise<void>;
  updateProfile: (data: Partial<ProfileData>) => Promise<void>;
  updateAvatar: (file: File) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  toggleTwoFactor: (enabled: boolean) => Promise<void>;
  updatePreferences: (prefs: Partial<ProfileData>) => Promise<void>;
  updateNotifications: (notifs: Partial<ProfileData>) => Promise<void>;
  loadLoginHistory: () => Promise<void>;
  setLoading: (loading: boolean) => void;
}

// Mock initial data
const mockProfileData: ProfileData = {
  fullName: 'Nguyen Van A',
  email: 'nguyenvana@example.com',
  phone: '+84 912 345 678',
  dateOfBirth: '2005-03-15',
  school: 'Trường THPT Lê Quý Đôn',
  gradeLevel: '12',
  address: '123 Đường ABC, Quận 1, TP.HCM',
  studentId: 'HS2024001',
  joinDate: '2024-01-15',
  avatarUrl: '',
  twoFactorEnabled: false,
  lastPasswordChange: '2024-09-15',
  examLanguage: 'vi',
  examFontSize: 'medium',
  autoSaveInterval: 30,
  showTimer: true,
  emailNotifications: true,
  examReminders: true,
  resultsNotifications: true,
  weeklyProgress: false,
  totalExams: 24,
  averageScore: 85.5,
  studyHours: 127,
  achievements: 12,
};

const mockLoginHistory: LoginHistoryItem[] = [
  {
    id: '1',
    device: 'Chrome on Windows',
    location: 'Ho Chi Minh City, Vietnam',
    date: '2025-10-02 14:30',
    ip: '113.161.xxx.xxx',
  },
  {
    id: '2',
    device: 'Safari on iPhone',
    location: 'Ho Chi Minh City, Vietnam',
    date: '2025-10-01 09:15',
    ip: '113.161.xxx.xxx',
  },
  {
    id: '3',
    device: 'Chrome on Windows',
    location: 'Ho Chi Minh City, Vietnam',
    date: '2025-09-30 16:45',
    ip: '113.161.xxx.xxx',
  },
  {
    id: '4',
    device: 'Chrome on Android',
    location: 'Hanoi, Vietnam',
    date: '2025-09-28 11:20',
    ip: '14.232.xxx.xxx',
  },
];

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      profile: null,
      loginHistory: [],
      isLoading: false,

      loadProfile: async () => {
        set({ isLoading: true });
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 800));
        set({
          profile: mockProfileData,
          isLoading: false,
        });
      },

      updateProfile: async (data: Partial<ProfileData>) => {
        set({ isLoading: true });
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));

        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: { ...currentProfile, ...data },
            isLoading: false,
          });
        }
      },

      updateAvatar: async (file: File) => {
        set({ isLoading: true });
        // Simulate file upload
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Create mock URL for uploaded file
        const mockUrl = URL.createObjectURL(file);

        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: { ...currentProfile, avatarUrl: mockUrl },
            isLoading: false,
          });
        }
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        set({ isLoading: true });
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1200));

        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: {
              ...currentProfile,
              lastPasswordChange: new Date().toISOString().split('T')[0],
            },
            isLoading: false,
          });
        }
      },

      toggleTwoFactor: async (enabled: boolean) => {
        set({ isLoading: true });
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));

        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: { ...currentProfile, twoFactorEnabled: enabled },
            isLoading: false,
          });
        }
      },

      updatePreferences: async (prefs: Partial<ProfileData>) => {
        set({ isLoading: true });
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 600));

        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: { ...currentProfile, ...prefs },
            isLoading: false,
          });
        }
      },

      updateNotifications: async (notifs: Partial<ProfileData>) => {
        set({ isLoading: true });
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 600));

        const currentProfile = get().profile;
        if (currentProfile) {
          set({
            profile: { ...currentProfile, ...notifs },
            isLoading: false,
          });
        }
      },

      loadLoginHistory: async () => {
        set({ isLoading: true });
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 700));
        set({
          loginHistory: mockLoginHistory,
          isLoading: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'profile-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        profile: state.profile,
      }),
    }
  )
);
